import { useTranslation } from 'react-i18next';
import type { StockLevel } from '@optiflow/types';
import { Badge } from '@/components/ui/Badge';

/**
 * The stock state of one line.
 *
 * Out of stock is its own state rather than an extreme of "low": a shelf with nothing on it is a
 * different conversation with a customer than one that is merely running down.
 */
export function StockStateBadge({ level }: { level: StockLevel }) {
  const { t } = useTranslation();

  if (level.isOutOfStock) return <Badge tone="danger">{t('inventory.states.outOfStock')}</Badge>;
  if (level.isLowStock) return <Badge tone="warning">{t('inventory.states.lowStock')}</Badge>;

  return <Badge tone="success">{t('inventory.states.inStock')}</Badge>;
}

/** Expiry proximity for a lot. */
export function ExpiryBadge({ daysUntilExpiry }: { daysUntilExpiry: number | null }) {
  const { t } = useTranslation();

  if (daysUntilExpiry === null) return <span className="text-muted text-xs">—</span>;

  if (daysUntilExpiry < 0) {
    return <Badge tone="danger">{t('inventory.states.expired', { days: -daysUntilExpiry })}</Badge>;
  }
  if (daysUntilExpiry <= 30) {
    return (
      <Badge tone="danger">{t('inventory.states.expiresInDays', { days: daysUntilExpiry })}</Badge>
    );
  }
  if (daysUntilExpiry <= 90) {
    return (
      <Badge tone="warning">{t('inventory.states.expiresInDays', { days: daysUntilExpiry })}</Badge>
    );
  }

  return (
    <Badge tone="neutral">{t('inventory.states.expiresInDays', { days: daysUntilExpiry })}</Badge>
  );
}
